// docs/116 CI3: where a band stands for each of the inn's poses. My view walks my band to these; every
// other view walks another player's band to the same ones when its `village_presence.pose` says so.
// A band of n figures gets n stations; the first is the leader's. `taken` are stations other bands
// (or guests) already hold, so two bands at the fire stand round it rather than in one another.
import type { InnPlan, Station } from '../layout';
import type { Pt } from '../walk';
import { POSE, type PoseCode } from './types';

const near = (a: Pt, b: Pt, d = 0.55) => Math.abs(a.x - b.x) < d && Math.abs(a.y - b.y) < 0.45;

/** Up to `n` stations for a band in `pose`. `seat` is the band's dice stool (pose 5), `bed` its first bed upstairs. */
export function poseStations(plan: InnPlan, pose: PoseCode, n: number, taken: readonly Pt[] = [], opts: { seat?: number; window?: number } = {}): Station[] {
  const free = (list: readonly Station[]) => list.filter((s) => !taken.some((t) => near(t, s)));
  const fill = (first: Station[], around: Pt, face?: 1 | -1): Station[] => {
    const out = first.slice(0, n);
    // whoever is left stands in a loose ring a little back from the first
    for (let i = out.length; i < n; i++) {
      const k = i - first.length;
      const x = around.x + (k % 2 ? 1 : -1) * (0.7 + Math.floor(k / 2) * 0.8);
      const y = around.y + 0.55 + (k % 3) * 0.2;
      out.push({ x, y, ...(face ? { flip: face < 0 } : { flip: x > around.x }) });
    }
    return out;
  };
  switch (pose) {
    case POSE.hearth: return fill(free(plan.hearth), plan.fire);
    case POSE.armchair: {
      // the first in the free armchair, the rest stand by the fire beside it
      const chair = free(plan.armchairs)[0];
      const by = free(plan.hearth);
      return chair ? fill([chair, ...by], chair) : fill(by, plan.fire);
    }
    case POSE.window: {
      const w = plan.gaze[opts.window ?? 0] ?? plan.gaze[0];
      if (!w) return [];
      // docs/120 VM4: the window's own place only if nobody else stands there
      return fill([...free([w]), ...free([{ x: w.x + 0.8, y: w.y + 0.3 }, { x: w.x - 0.8, y: w.y + 0.3 }])], w);
    }
    case POSE.bar: return fill(free(plan.bar.stand), { x: plan.bar.x + plan.bar.w / 2, y: plan.bar.y + 0.9 });
    case POSE.dice: {
      // the band's player on their stool, the rest standing behind them to watch
      const st = plan.dice.stools[opts.seat ?? -1];
      const mid = { x: plan.dice.x + plan.dice.w / 2 - 0.5, y: plan.dice.y };
      const watch: Station[] = [{ x: mid.x - 1.6, y: mid.y + 0.75 }, { x: mid.x + 1.6, y: mid.y + 0.75, flip: true }, { x: mid.x - 0.6, y: mid.y + 1.25 }, { x: mid.x + 0.6, y: mid.y + 1.25, flip: true }, { x: mid.x - 2.3, y: mid.y + 0.2 }, { x: mid.x + 2.4, y: mid.y + 0.2, flip: true }];
      return fill([...(st ? [st] : []), ...free(watch)], mid);
    }
    case POSE.asleep: return plan.beds.map((b) => b.sleeper).slice(0, n);
    default: return [];
  }
}

/** Spectators round the dice table (anyone watching from the room). */
export function watchStations(plan: InnPlan, n: number, taken: readonly Pt[] = []): Station[] {
  return poseStations(plan, POSE.dice, n + 1, taken).slice(1);
}
